import React, { useState, lazy, Suspense } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import { SdpModeProvider } from '../context/SdpModeContext';
import { SessionSettingsProvider } from '../context/SessionSettingsContext';

// Lazy load page components
const Overview = lazy(() => import('../pages/Overview'));
const SmartScheduling = lazy(() => import('../pages/SmartScheduling'));
const PatientsNow = lazy(() => import('../pages/PatientsNow'));
const Insurance = lazy(() => import('../pages/Insurance'));
const TreatmentPlans = lazy(() => import('../pages/TreatmentPlans'));
const DentalLM = lazy(() => import('../pages/DentalLM'));
const Payments = lazy(() => import('../pages/Payments'));
const Forecasting = lazy(() => import('../pages/Forecasting'));
const SelfDrivingPractice = lazy(() => import('../pages/SelfDrivingPractice'));
const FeeOptimization = lazy(() => import('../pages/FeeOptimization'));

// Vet Pages
const VetDashboard = lazy(() => import('../vet/Dashboard'));
const VetScheduling = lazy(() => import('../vet/Scheduling'));
const VetPatientsNow = lazy(() => import('../vet/PatientsNow'));
const VetPreventiveCare = lazy(() => import('../vet/PreventiveCare'));
const VetCarePlans = lazy(() => import('../vet/CarePlans'));
const VetPetInsurance = lazy(() => import('../vet/PetInsurance'));
const VetFeeOptimization = lazy(() => import('../vet/FeeOptimization'));

// DSO Pages
const ControlTower = lazy(() => import('../dso/ControlTower'));
const TargetsPolicies = lazy(() => import('../dso/TargetsPolicies'));
const OptimizationRuns = lazy(() => import('../dso/OptimizationRuns'));
const OpenSlotMarketplace = lazy(() => import('../dso/OpenSlotMarketplace'));
const FloatPoolPlanner = lazy(() => import('../dso/FloatPoolPlanner'));
const WorkQueue = lazy(() => import('../dso/WorkQueue'));
const ScenarioPlanning = lazy(() => import('../dso/ScenarioPlanning'));
const TemplatesPlaybooks = lazy(() => import('../dso/TemplatesPlaybooks'));
const Explainability = lazy(() => import('../dso/Explainability'));


const Dashboard = ({ userRole, onLogout }) => {
    // Start with sidebar closed on mobile/tablet
    const getInitialSidebarState = () => {
        return window.innerWidth > 1024;
    };
    
    const [isSidebarOpen, setIsSidebarOpen] = useState(getInitialSidebarState);
    const [autopilotEnabled, setAutopilotEnabled] = useState(true);
    // Set default page based on user role
    const getDefaultPage = () => {
        if (userRole === 'dso') return 'control-tower';
        if (userRole === 'vet') return 'vet-dashboard';
        return 'dashboard';
    };
    const [currentPage, setCurrentPage] = useState(getDefaultPage());

   
    const menuFN = (details) => {      
        if (details === "logout") {
            // Call the logout function passed from App.js
            if (onLogout) {
                onLogout();
            }
        } else {
            setCurrentPage(details)
        }
    }

    const handleAutopilotToggle = () => {
        setAutopilotEnabled(!autopilotEnabled);
    };

    // Loading fallback component
    const LoadingFallback = () => (
        <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
        </div>
    );

    return (
        <SdpModeProvider>
            <SessionSettingsProvider>
                <div className="dashboard">
                <Header 
                    onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                    userRole={userRole} 
                    isSidebarOpen={isSidebarOpen}
                    autopilotEnabled={autopilotEnabled}
                    onAutopilotToggle={handleAutopilotToggle}
                />
                <Sidebar
                    isOpen={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(false)}
                    currentPage={currentPage}
                    onNavigate={menuFN}
                    userRole={userRole}
                />
                <div className={`main-content ${!isSidebarOpen ? 'sidebar-closed' : ''}`}>
                    <Suspense fallback={<LoadingFallback />}>
                    {/* Dental Pages */}
                    {currentPage === 'dashboard' && <Overview autopilotEnabled={autopilotEnabled} />}
                 
                    {(currentPage === 'scheduling' && userRole === 'dental') && <SmartScheduling />}                  
                    {(currentPage === 'patients' && userRole === 'dental') && <PatientsNow />}
                    {(currentPage === 'insurance' && userRole === 'dental') && <Insurance />}
                    {(currentPage === 'treatment' && userRole === 'dental') && <TreatmentPlans />}
                    {(currentPage === 'forecasting' && userRole === 'dental') &&<Forecasting />}
                    {(currentPage === 'selfdrivingpractice' && (userRole === 'dental' || userRole === 'vet')) &&  <SelfDrivingPractice />}
                    {(currentPage === 'payments') && <Payments />}
                    {(currentPage === 'fee-optimization' && userRole === 'dental') && <FeeOptimization />}
                    {(currentPage === 'dentallm' && userRole === 'dental') && <DentalLM />}
                    
                    {/* Vet Pages */}                 
                    {(currentPage === 'vet-dashboard' && userRole === 'vet') && <VetDashboard />}
                    {currentPage === 'scheduling' && userRole === 'vet' && <VetScheduling />}                   
                    {currentPage === 'patients' && userRole === 'vet' && <VetPatientsNow />}
                    {currentPage === 'preventivecare' && userRole === 'vet' && <VetPreventiveCare />}
                    {currentPage === 'careplans' && userRole === 'vet' && <VetCarePlans />}
                    {currentPage === 'insurance' && userRole === 'vet' && <VetPetInsurance />}                   
                    {currentPage === 'fee-optimization' && userRole === 'vet' && <VetFeeOptimization />}                   
                   
                    {/* DSO Pages */}
                    {currentPage === 'control-tower' && <ControlTower />}
                    {currentPage === 'targets-policies' && <TargetsPolicies />}
                    {currentPage === 'optimization-runs' && <OptimizationRuns />}
                    {currentPage === 'open-slot-marketplace' && <OpenSlotMarketplace />}
                    {currentPage === 'float-pool-planner' && <FloatPoolPlanner />}
                    {currentPage === 'work-queue' && <WorkQueue />}
                    {currentPage === 'scenario-planning' && <ScenarioPlanning />}
                    {currentPage === 'templates-playbooks' && <TemplatesPlaybooks />}
                    {currentPage === 'explainability' && <Explainability />}
                </Suspense>
            </div>
        </div>
        </SessionSettingsProvider>
        </SdpModeProvider>
    );
};

export default Dashboard;
